/* eslint-disable prettier/prettier */
import * as React from 'react';
import { useEffect } from 'react';
import {
  CaretSortIcon,
  ChevronDownIcon,
  DotsHorizontalIcon,
} from '@radix-ui/react-icons';
import {
  ColumnDef,
  ColumnFiltersState,
  PaginationState,
  SortingState,
  VisibilityState,
  flexRender,
  getCoreRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';

import { Button } from 'app/components/ui/button';

import { Input } from 'app/components/ui/input';
import { Toaster } from 'app/components/ui/toaster';
import { SearchIcon } from 'lucide-react';
import { Badge } from 'app/components/ui/badge';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'app/components/ui/dropdown-menu';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'app/components/ui/table';
import { useDispatch } from 'react-redux';
import useDebounce from 'utils/hooks/debounce-hook';
import { useProductsSlice } from '../slice';
import { endpoints, mediaBaseUrl } from 'utils/api/endpoints';
import { PRODUCT_STATUS } from 'utils/constants';
import { AlertDelete } from './delete';
import { useNavigate } from 'react-router-dom';

interface DataTableProps {
  statusFilter?: { status?: number };
  productTypeFilter?: { product_type?: number };
  activeTab?: string;
  onTabCountsUpdate?: (counts: { all: number; active: number; inactive: number; draft: number; archived: number }) => void;
}

export function DataTable({ statusFilter = {}, productTypeFilter = {}, activeTab = 'all', onTabCountsUpdate }: DataTableProps) {
  const { useGetProductsQuery } = useProductsSlice();
  const [getProducts, { data, error, isLoading }] = useGetProductsQuery();
  const [open, setOpen] = React.useState(false);
  const navigate = useNavigate();
  const [tabCounts, setTabCounts] = React.useState({ all: 0, active: 0, inactive: 0, draft: 0, archived: 0 });

  const handleEdit = (product: any) => {
    const currentUrl = new URL(window.location.href);
    const currentTab = currentUrl.searchParams.get('status') || 'all';
    const currentProductType = currentUrl.searchParams.get('product-type');

    let editUrl = `/products/edit?id=${product.product_id}`;

    // Preserve tab context
    if (currentTab !== 'all') {
      editUrl += `&status=${currentTab}`;
    }

    // Preserve product type context
    if (currentProductType) {
      editUrl += `&product-type=${currentProductType}`;
    }

    navigate(editUrl);
  };

  // Function to update tab counts from API response
  const updateTabCounts = React.useCallback((responseData: any) => {
    if (responseData?.response?.statusCounts) {
      const statusCounts = responseData.response.statusCounts;
      const counts = {
        all: parseInt(statusCounts.total) || 0,
        active: parseInt(statusCounts.active) || 0,
        inactive: parseInt(statusCounts.inactive) || 0,
        draft: parseInt(statusCounts.draft) || 0,
        archived: parseInt(statusCounts.archived) || 0,
      };

      setTabCounts(counts);
      onTabCountsUpdate?.(counts);
    }
  }, [onTabCountsUpdate]);

  const columns: ColumnDef<any>[] = [
    {
      accessorKey: 'product_name',
      header: 'Product',
      cell: ({ row }) => {
        const coverImage = row.original?.media?.find((m: any) => m.is_cover === 1);
        const imageUrl = mediaBaseUrl + '/products/' + coverImage?.file_name || '/images/default-user-image.png';

        return (
          <div className="flex items-center space-x-3">
            <img
              src={imageUrl}
              alt={row.getValue('product_name')}
              className="w-12 h-12 rounded-lg object-cover border border-gray-200 dark:border-gray-600"
              loading="lazy"
            />
            <div className="flex flex-col">
              <span className="font-medium text-sm text-gray-900 dark:text-gray-100">{row.getValue('product_name')}</span>
              <span className="text-xs text-gray-500 dark:text-gray-400">{row.original.sku}</span>
            </div>
          </div>
        );
      },
    },
    {
      accessorKey: 'type',
      header: 'Type',
      cell: ({ row }) => {
        const productType = row.original.product_type;
        const productTypeConfig = {
          1: {
            label: 'Digital Product',
            className: 'bg-blue-100 dark:bg-blue-900 text-blue-800 dark:text-blue-200',
          },
          2: {
            label: 'Physical Product',
            className: 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200',
          },
        };
        const config = productTypeConfig[productType];
        return (
          <Badge className={config?.className || 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200'}>
            {config?.label || 'Unknown'}
          </Badge>
        );
      },
    },
    {
      accessorKey: 'category',
      header: 'Category',
      cell: ({ row }) => {
        const categories = row.original.categories || [];
        const subCategories = categories.filter((cat: any) => cat.category_name !== 'Physical Products' && cat.category_name !== 'Digital Products');

        return (
          <div className="flex flex-wrap gap-1">
            {subCategories.slice(0, 2).map((category: any) => (
              <span
                key={category.category_id}
                className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded-full"
              >
                {category.category_name}
              </span>
            ))}
            {subCategories.length > 2 && (
              <span className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded-full">
                +{subCategories.length - 2}
              </span>
            )}
            {subCategories.length === 0 && (
              <span className="text-xs text-gray-400 dark:text-gray-500">No categories</span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'sale_price',
      header: 'Pricing',
      cell: ({ row }) => {
        const salePrice = row.original.sale_price;
        const originalPrice = row.original.original_price;

        return (
          <div className="flex flex-col">
            {salePrice ? (
              <>
                <span className="font-semibold text-green-600 dark:text-green-400">${salePrice}</span>
                <span className="text-xs text-gray-500 dark:text-gray-400 line-through">${originalPrice}</span>
              </>
            ) : (
              <span className="font-semibold text-gray-900 dark:text-gray-100">${originalPrice}</span>
            )}
          </div>
        );
      },
    },
    {
      accessorKey: 'stock_quantity',
      header: 'Stock',
      cell: ({ row }) => {
        const stock = row.getValue('stock_quantity');
        const isLowStock = stock < 20;
        const isOutOfStock = stock === 0;

        return (
          <div className="flex flex-col">
            <span className={`font-medium ${isOutOfStock ? 'text-red-600 dark:text-red-400' : isLowStock ? 'text-orange-600 dark:text-orange-400' : 'text-green-600 dark:text-green-400'}`}>
              {stock}
            </span>
            {isOutOfStock && <span className="text-xs text-red-500 dark:text-red-400">Out of Stock</span>}
            {isLowStock && !isOutOfStock && <span className="text-xs text-orange-500 dark:text-orange-400">Low Stock</span>}
          </div>
        );
      },
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }) => (
        <Badge
          variant={row.getValue('status') === 2 ? 'default' : 'secondary'}
          className={
            row.getValue('status') === 2
              ? 'bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200'
              : row.getValue('status') === 1
                ? 'bg-yellow-100 dark:bg-yellow-900 text-yellow-800 dark:text-yellow-200'
                : row.getValue('status') === 3
                  ? 'bg-red-100 dark:bg-red-900 text-red-800 dark:text-red-200'
                  : row.getValue('status') === 4
                    ? 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200'
          }
        >
          {PRODUCT_STATUS[row.getValue('status')] || 'Unknown'}
        </Badge>
      ),
    },
    {
      accessorKey: 'created_at',
      header: 'Created At',
      cell: ({ row }) => {
        const createdAt = row.getValue('created_at');
        if (!createdAt) return <span className="text-gray-400 dark:text-gray-500">-</span>;

        const date = new Date(createdAt as string);
        return (
          <div className="flex flex-col">
            <span className="text-sm text-gray-900 dark:text-gray-100">
              {date.toLocaleDateString()}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: 'updated_at',
      header: 'Updated At',
      cell: ({ row }) => {
        const updatedAt = row.getValue('updated_at');
        if (!updatedAt) return <span className="text-gray-400 dark:text-gray-500">-</span>;

        const date = new Date(updatedAt as string);
        return (
          <div className="flex flex-col">
            <span className="text-sm text-gray-900 dark:text-gray-100">
              {date.toLocaleDateString()}
            </span>
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
            </span>
          </div>
        );
      },
    },
    {
      accessorKey: '',
      header: 'Actions',
      cell: ({ row }) => (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" className="h-8 w-8 p-0">
              <DotsHorizontalIcon className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Actions</DropdownMenuLabel>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={() => handleEdit(row.original)}>
              Edit Product
            </DropdownMenuItem>
            <DropdownMenuItem onClick={() => handleDelete(row.original)}>
              Delete Product
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ),
    },
  ];

  const labelsPagination = {
    totalPages: data?.response?.totalPages,
    pageSize: data?.response?.perPage,
  };
  const labels = React.useMemo(() => {
    const productsData = data?.response?.data || [];
    const productType = productTypeFilter.product_type;

    if (productType) {
      console.log('Client-side filtering by product type:', productType);
      return productsData.filter((product: any) => product.product_type === productType);
    }

    return productsData;
  }, [data?.response?.data, productTypeFilter]);
  const dispatch = useDispatch();

  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 20,
  });

  const [search, setSearch] = React.useState('');
  const searchDebounce = useDebounce(search, 300);

  const [selected, setSelected] = React.useState<number>(0);
  const [rowSelected, setRowSelected] = React.useState<any>([]);

  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [columnFilters, setColumnFilters] = React.useState<ColumnFiltersState>(
    [],
  );
  const [columnVisibility, setColumnVisibility] =
    React.useState<VisibilityState>({});
  const [rowSelection, setRowSelection] = React.useState({});

  const table = useReactTable({
    data: labels,
    columns,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    getCoreRowModel: getCoreRowModel(),
    // getPaginationRowModel: getPaginationRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getFilteredRowModel: getFilteredRowModel(),
    onColumnVisibilityChange: setColumnVisibility,
    onRowSelectionChange: setRowSelection,
    manualPagination: true,
    pageCount: labelsPagination?.totalPages,
    rowCount: labelsPagination?.pageSize,
    onPaginationChange: setPagination,
    autoResetPageIndex: false,
    state: {
      sorting,
      columnFilters,
      columnVisibility,
      rowSelection,
      pagination,
    },
  });

  // Update tab counts when data changes
  useEffect(() => {
    if (data) {
      updateTabCounts(data);
    }
  }, [data, updateTabCounts]);

  // Fetch products when status filter changes
  useEffect(() => {
    const params = {
      page: pagination.pageIndex + 1,
      perPage: pagination.pageSize,
      search: searchDebounce,
      ...statusFilter,
      ...productTypeFilter,
    };
    console.log('API Call Params:', params);
    getProducts(params);
  }, [statusFilter, productTypeFilter, pagination.pageIndex, pagination.pageSize, searchDebounce, getProducts]);

  // Initial data fetch on component mount
  useEffect(() => {
    getProducts({
      page: pagination.pageIndex + 1,
      perPage: pagination.pageSize,
      search: searchDebounce,
      ...statusFilter,
      ...productTypeFilter,
    });
  }, []); // Empty dependency array for initial load only

  const handleDelete = (product: any) => {
    setOpen(true);
    setRowSelected(product);
  };

  return (
    <div className="w-full rounded-lg bg-white dark:bg-gray-900 shadow-sm border border-gray-200 dark:border-gray-700">
      <div className="p-6">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
          <div className="relative w-full sm:w-auto">
            <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
              <SearchIcon className="h-4 w-4 text-gray-400 dark:text-gray-500" />
            </div>
            <Input
              placeholder="Search products by name or SKU..."
              value={search}
              onChange={e => setSearch(e.target.value)}
              className="pl-10 w-full sm:w-80 bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-600 text-gray-900 dark:text-gray-100"
            />
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
            <span>Total: {data?.response?.totalCount || 0} products</span>
          </div>
        </div>

        <div className="rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader className="bg-gray-50 dark:bg-gray-800">
                {table.getHeaderGroups().map(headerGroup => (
                  <TableRow key={headerGroup.id} className="border-gray-200 dark:border-gray-700">
                    {headerGroup.headers.map(header => {
                      return (
                        <TableHead key={header.id} className="font-semibold text-gray-900 dark:text-gray-100">
                          {header.isPlaceholder
                            ? null
                            : flexRender(
                              header.column.columnDef.header,
                              header.getContext(),
                            )}
                        </TableHead>
                      );
                    })}
                  </TableRow>
                ))}
              </TableHeader>
              <TableBody>
                {isLoading ? (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-32 text-center"
                    >
                      <div className="flex items-center justify-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <span className="ml-2">Loading products...</span>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : table.getRowModel().rows?.length ? (
                  table.getRowModel().rows.map(row => (
                    <TableRow
                      key={row.id}
                      data-state={row.getIsSelected() && 'selected'}
                      className="hover:bg-gray-50 dark:hover:bg-gray-800 border-gray-200 dark:border-gray-700"
                    >
                      {row.getVisibleCells().map(cell => (
                        <TableCell key={cell.id} className="py-4">
                          {flexRender(
                            cell.column.columnDef.cell,
                            cell.getContext(),
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell
                      colSpan={columns.length}
                      className="h-32 text-center text-gray-500 dark:text-gray-400"
                    >
                      <div className="flex flex-col items-center justify-center">
                        <div className="text-4xl mb-2">📦</div>
                        <p className="text-lg font-medium">No products found</p>
                        <p className="text-sm">Try adjusting your search or filters</p>
                      </div>
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        <div className="flex flex-col sm:flex-row items-center justify-between space-y-2 sm:space-y-0 py-4">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Showing page {pagination.pageIndex + 1} of {table.getPageCount()}
            {data?.response?.totalCount && (
              <span> ({data.response.totalCount} total products)</span>
            )}
          </div>
          <div className="flex items-center space-x-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.firstPage()}
              disabled={!table.getCanPreviousPage()}
              className="border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              First
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.previousPage()}
              disabled={!table.getCanPreviousPage()}
              className="border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.nextPage()}
              disabled={!table.getCanNextPage()}
              className="border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Next
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => table.lastPage()}
              disabled={!table.getCanNextPage()}
              className="border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700"
            >
              Last
            </Button>
          </div>
        </div>
      </div>

      <Toaster />
      <AlertDelete isOpen={open} onOpenChange={setOpen} data={rowSelected} />
    </div>
  );
}
